import { Injectable } from '@angular/core';
import { mapToVoid } from '@examdojo/core/rxjs';
import { mapQuestionBankQuestionHttpModelToStoreModel } from '@examdojo/question-bank';
import { setEntities } from '@ngneat/elf-entities';
import { Observable, of, tap } from 'rxjs';
import { AssignmentSectionWithQuestionsHttpModel, ClassAssignmentQuery, SaveSectionData } from '../class-assignment';
import { ClassAssignmentHttpService } from '../class-assignment/class-assignment-http.service';
import { AssignmentBuilderSectionStoreModel, AssignmentSectionType } from './assignment-builder.model';
import { AssignmentBuilderStore } from './assignment-builder.store';

@Injectable({ providedIn: 'root' })
export class AssignmentBuilderService {
  constructor(
    private readonly store: AssignmentBuilderStore,
    private readonly classAssignmentHttpService: ClassAssignmentHttpService,
    private readonly classAssignmentQuery: ClassAssignmentQuery,
  ) {}

  createSection(id: string, data: Partial<AssignmentBuilderSectionStoreModel>): void {
    this.store.create({
      id,
      type: AssignmentSectionType.Manual,
      questions: [],
      ...data,
    });
    this.store.updateProp('isDirty', true);
  }

  updateSection(id: string, data: Partial<AssignmentBuilderSectionStoreModel>): void {
    this.store.update(id, data);
    this.store.updateProp('isDirty', true);
  }

  removeSection(sectionId: string): void {
    this.store.delete(sectionId);
    this.store.updateProp('isDirty', true);
  }

  moveSection(previousIndex: number, currentIndex: number): void {
    if (previousIndex === currentIndex) {
      return;
    }

    const state = this.store.getValue();
    const ids = [...state.ids];
    const [removed] = ids.splice(previousIndex, 1);
    ids.splice(currentIndex, 0, removed);

    const reorderedEntities = ids.map((id) => state.entities[id]);
    this.store.updateStore(setEntities(reorderedEntities));
    this.store.updateProp('isDirty', true);
  }

  removeQuestion(sectionId: string, questionId: number): void {
    this.store.update(sectionId, (section) => ({
      ...section,
      questions: section.questions.filter((q) => q.id !== questionId),
    }));
    this.store.updateProp('isDirty', true);

    if (this.store.getValue().entities[sectionId]?.questions.length === 0) {
      this.removeSection(sectionId);
    }
  }

  setWeeklyPublishLimitExceeded(exceeded: boolean): void {
    this.store.updateProp('weeklyPublishLimitExceeded', exceeded);
  }

  reset(): void {
    this.store.reset();
  }

  init(classAssignmentId: string): Observable<void> {
    return this.classAssignmentHttpService.fetchSections(classAssignmentId).pipe(
      tap((sections) => {
        this.store.set(sections.map((section) => this.mapSectionToStoreModel(section)));
        this.store.updateProp('isDirty', false);
      }),
      mapToVoid(),
    );
  }

  saveDraft(dueAt?: Date | null, scheduledAtDraft?: Date | null): Observable<void> {
    const assignmentId = this.getActiveAssignmentId();
    const sectionsData = this.getSectionsData();

    const save$ =
      dueAt !== undefined
        ? this.classAssignmentHttpService.saveDraft(assignmentId, dueAt, scheduledAtDraft ?? null, sectionsData)
        : this.store.getValue().props.isDirty
          ? this.classAssignmentHttpService.saveSections(assignmentId, sectionsData)
          : of(undefined);

    return save$.pipe(
      tap(() => {
        this.store.updateProp('isDirty', false);
      }),
      mapToVoid(),
    );
  }

  private getActiveAssignmentId(): string {
    const activeAssignment = this.classAssignmentQuery.getActive();
    if (!activeAssignment?.assignment_id) {
      throw new Error('No active assignment found');
    }
    return activeAssignment.assignment_id;
  }

  private getSectionsData(): SaveSectionData[] {
    const state = this.store.getValue();
    return state.ids.map((id, index) => {
      const section = state.entities[id];
      return {
        id: section.id,
        name: section.id,
        position: index,
        questionIds: section.questions.map((q: { id: number }) => q.id),
      };
    });
  }

  private mapSectionToStoreModel(section: AssignmentSectionWithQuestionsHttpModel): AssignmentBuilderSectionStoreModel {
    return {
      id: section.id!,
      type: AssignmentSectionType.Manual,
      questions: section.questions.map((q) => mapQuestionBankQuestionHttpModelToStoreModel(q)),
    };
  }
}
